import store from '@/store/store'
import { queryClient } from '@network/queryClient'
import { logger } from '@logger'
import { getSocket } from './socketConnection'
import { SERVER_EVENTS, GAME_STATUS, getGameRoom } from './socketEvents'
import {
  setScheduled,
  setAcceptBet,
  setStarting,
  setRunning,
  setEnded,
  setMaintenance,
} from '@slices/eventsSlice'
import { updateBalance } from '@slices/walletSlice'
import { setLastCrashMultiplier } from '@slices/eventsSlice'
import { emitLastMultipliers } from './socketEmitters'
import {
  onScheduled as onClassicScheduled,
  onAcceptBet as onClassicAcceptBet,
  onRunning as onClassicRunning,
  onEnded as onClassicEnded,
  onPlaceBetResponse as onClassicPlaceBetResponse,
  onCashoutResponse as onClassicCashoutResponse,
  onBetSettled as onClassicBetSettled,
  hasPendingEmit as classicHasPendingEmit,
} from '@core/betting/classicBettingOrchestrator'
import {
  onScheduled as onFreebetScheduled,
  onRunning as onFreebetRunning,
  onEnded as onFreebetEnded,
  onPlaceBetResponse as onFreebetPlaceBetResponse,
  onCashoutResponse as onFreebetCashoutResponse,
  onBetSettled as onFreebetBetSettled,
  hasPendingEmit as freebetHasPendingEmit,
} from '@core/betting/freebetBettingOrchestrator'
import { armBetSubmission, disarmBetSubmission } from '@core/betting/betSubmissionScheduler'

const MODULE = 'Socket Listeners'

// The dynamic game-state event name is built at registration time and kept
// here so `removeListeners` can unbind exactly what `registerListeners` bound.
let boundGameRoom = null

/* ───────────────────────── Game-state ticks ─────────────────────────────────
 * The ONLY stream funneled into Redux (eventsSlice). Arrives on the dynamic
 * `${gameCode}/${gameMode}` event as { event, ts, data:{ status, ... } }.
 * ───────────────────────────────────────────────────────────────────────── */
const handleGameState = (msg) => {
  const data = msg?.data || {}
  console.log('BETDEBUG-context: game-state tick', { status: data.status, data, receivedAt: Date.now() })
  if (data.status === GAME_STATUS.ACCEPT_BET) {
    console.log('BETDEBUG-3: ACCEPT_BET received — round is open for betting', {
      data,
      localTime: Date.now(),
      localTimeISO: new Date().toISOString(),
    })
  }
  switch (data.status) {
    case GAME_STATUS.SCHEDULED:
      store.dispatch(setScheduled(data))
      disarmBetSubmission()
      onClassicScheduled()
      onFreebetScheduled()
      break
    case GAME_STATUS.ACCEPT_BET:
      store.dispatch(setAcceptBet(data))
      // Order matters: the auto loop re-queues its bet first, then the scheduler
      // opens the window — so a freshly re-armed auto bet is picked up by THIS
      // round's submission (which may fire immediately on a short window).
      onClassicAcceptBet()
      armBetSubmission(data)
      break
    case GAME_STATUS.STARTING:
      store.dispatch(setStarting(data))
      disarmBetSubmission() // betting is closed — nothing more goes out this round
      break
    case GAME_STATUS.RUNNING:
      store.dispatch(setRunning(data))
      disarmBetSubmission()
      onClassicRunning()
      onFreebetRunning()
      break
    case GAME_STATUS.ENDED:
      store.dispatch(setEnded(data))
      disarmBetSubmission()
      onClassicEnded()
      onFreebetEnded()
      emitLastMultipliers()
      break
    case GAME_STATUS.UNDER_MAINTENANCE:
      store.dispatch(setMaintenance(data))
      disarmBetSubmission()
      break
    default:
      logger.warn(MODULE, 'Unknown game status', data)
  }
}

/* ───────────────────────── Private: balance ─────────────────────────────────
 * Routed into the existing walletSlice so balance stays in one place.
 * ───────────────────────────────────────────────────────────────────────── */
const handleBalanceUpdate = (msg) => {
  const balance = msg?.data?.balance
  logger.info(MODULE, 'USER_BALANCE_UPDATE', msg?.data)
  if (typeof balance === 'number') store.dispatch(updateBalance(balance))
}

/* ───────────────────────── Command replies ──────────────────────────────────
 * NOT stored in Redux (by design). For now we log and toast on failure.
 * When you need to act on a reply, call your follow-up at the EXTENSION POINT.
 * Command envelope: { status, success, message, code, data }.
 * ───────────────────────────────────────────────────────────────────────── */
const handlePlaceBetResponse = (msg) => {
  logger.info(MODULE, 'PLACE_BET_RESPONSE', msg)
  // A place-reply carries no bet-type discriminator, so route it to whichever
  // orchestrator is awaiting one. Only one ever is: Classic and Freebets are
  // mutually exclusive while busy (tab lock). If neither expects it, it's a
  // stray/duplicate — ignore rather than let the panel-echo shortcut mis-claim it.
  if (freebetHasPendingEmit()) onFreebetPlaceBetResponse(msg)
  else if (classicHasPendingEmit()) onClassicPlaceBetResponse(msg)
  else logger.warn(MODULE, 'PLACE_BET_RESPONSE with no pending emit — ignored', msg?.data)

  // Every accepted bet (manual, auto, or freebet — all go through this one
  // event) contributes to the jackpot pool. Refresh it now instead of waiting
  // for the header bar's next poll tick (mirrors defenderV2's
  // setFetchActiveJackpotFlag-after-placement).
  if (msg?.success && store.getState().configurations.featureToggles.jackpot) {
    queryClient.invalidateQueries({ queryKey: ['jackpot', 'currentUserSpecific'] })
  }
}

const handleCashoutResponse = (msg) => {
  logger.info(MODULE, 'CASHOUT_RESPONSE', msg)
  // Correlated by transactionId — each orchestrator no-ops if it's not its bet.
  onClassicCashoutResponse(msg)
  onFreebetCashoutResponse(msg)
}

/* ───────────── Bootstrap / broadcasts / settlement (listen-only) ─────────────
 * Registered so the stream is observable now; each just logs. Plug your own
 * handler at the EXTENSION POINT when you need the data. Event envelope:
 * { event, ts, data }.
 * ───────────────────────────────────────────────────────────────────────── */
const handleGameStats = (msg) => {
  logger.info(MODULE, 'GAME_STATS', msg?.data)
  // EXTENSION POINT: hydrate current round snapshot on (re)connect.
}

const handleActiveBet = (msg) => {
  logger.info(MODULE, 'ACTIVE_BET', msg?.data)
  // EXTENSION POINT: restore the player's open bets after a refresh/reconnect.
}

const handleLastMultipliers = (msg) => {
  logger.info(MODULE, 'LAST_MULTIPLIERS', msg?.data)
  store.dispatch(setLastCrashMultiplier({ lastCrashMultipliers: msg?.data }))
  // EXTENSION POINT: render crash-point history.
}

const handleBetPlaced = (msg) => {
  logger.info(MODULE, 'BET_PLACED', msg?.data)
  // EXTENSION POINT: add to the live "other players" feed.
}

const handleCashedOut = (msg) => {
  logger.info(MODULE, 'CASHED_OUT', msg?.data)
  // EXTENSION POINT: mark cashed-out in the live feed.
}

const handleBetSettled = (msg) => {
  logger.info(MODULE, 'BET_SETTLED', msg?.data)
  // Authoritative final record (WON / LOST / CASHED_OUT). Let each orchestrator
  // safety-reconcile its own panel (matched by transactionId; the other no-ops).
  // The freebet orchestrator additionally re-fetches ['freebets'] since the BE
  // pushes no freebet-list update when a slot is consumed.
  onClassicBetSettled(msg)
  onFreebetBetSettled(msg)
  // Invalidate the bet history so "My Transactions" refetches and shows the
  // freshly settled bet. Prefix match covers every page/filter variant; only
  // mounted queries refetch.
  queryClient.invalidateQueries({ queryKey: ['bets'] })
}

// Map of every server->client event to its handler. Add new events here.
const buildHandlerMap = () => ({
  [SERVER_EVENTS.USER_BALANCE_UPDATE]: handleBalanceUpdate,
  [SERVER_EVENTS.PLACE_BET_RESPONSE]: handlePlaceBetResponse,
  [SERVER_EVENTS.CASHOUT_RESPONSE]: handleCashoutResponse,
  [SERVER_EVENTS.GAME_STATS]: handleGameStats,
  [SERVER_EVENTS.ACTIVE_BET]: handleActiveBet,
  [SERVER_EVENTS.LAST_MULTIPLIERS]: handleLastMultipliers,
  [SERVER_EVENTS.BET_PLACED]: handleBetPlaced,
  [SERVER_EVENTS.CASHED_OUT]: handleCashedOut,
  [SERVER_EVENTS.BET_SETTLED]: handleBetSettled,
})

/**
 * Bind all server->client handlers. Idempotent — it removes any existing
 * bindings first, so StrictMode double-mounts and reconnects never stack
 * duplicate listeners.
 *
 * @param {{ gameCode: string, gameMode: string }} params
 */
export const registerListeners = ({ gameCode, gameMode }) => {
  const socket = getSocket()
  if (!socket) {
    logger.error(MODULE, 'Cannot register listeners: no socket instance')
    return
  }

  removeListeners()

  // dynamic game-state tick (the room name itself)
  boundGameRoom = getGameRoom(gameCode, gameMode)
  socket.on(boundGameRoom, handleGameState)

  // everything else
  const handlers = buildHandlerMap()
  Object.entries(handlers).forEach(([event, handler]) => socket.on(event, handler))

  logger.success(MODULE, 'Listeners registered', { gameRoom: boundGameRoom })
}

// Unbind everything `registerListeners` bound (same handler references).
export const removeListeners = () => {
  // Drop the armed submission timer first: without a socket it could not emit
  // anyway, and a stale timer must not fire into a torn-down connection.
  disarmBetSubmission()

  const socket = getSocket()
  if (!socket) return

  if (boundGameRoom) {
    socket.off(boundGameRoom, handleGameState)
    boundGameRoom = null
  }

  const handlers = buildHandlerMap()
  Object.entries(handlers).forEach(([event, handler]) => socket.off(event, handler))

  logger.info(MODULE, 'Listeners removed')
}
